import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { AuthenticatedConnectUser, paragon, SDK_EVENT } from "lib/integrations/paragon";
import { useIntegrationSdkToken } from "./useIntegrationSdkToken";

/**
 * Prefix for the cached connected user. The token is part of the full key, so a refresh starts a
 * new entry; SDK events are written across every entry under this prefix.
 */
const PARAGON_USER_QUERY_KEY = ["paragonUser"];

/**
 * The Paragon connected user, authenticating the SDK on first use.
 *
 * Cached rather than held per caller: `paragon.authenticate` mutates one module-level SDK, so
 * several components asking for the user should not each make the round trip — and should not be
 * able to disagree about the connection state while each re-renders.
 */
export default function useParagonUser(
  /**
   * Off for a surface that only might need Paragon. Authenticating costs a token request and an
   * SDK round trip, and a caller that is not offering a Paragon CRM gets nothing for either.
   */
  enabled = true,
): { user?: AuthenticatedConnectUser; error?: Error } {
  const projectId = window.paragonProjectId;
  const { sdkToken, isError } = useIntegrationSdkToken(enabled);
  const { data: currentUser } = useCurrentUser();
  const queryClient = useQueryClient();

  const connectionName = currentUser
    ? `${currentUser.company.name}${currentUser.company.demo ? " (Demo)" : ""}`
    : undefined;

  const { data: user, error } = useQuery(
    [...PARAGON_USER_QUERY_KEY, projectId, sdkToken, connectionName],
    async () => {
      // Guarded by `enabled`, so these are set by the time the query runs.
      await paragon.authenticate(projectId as string, sdkToken as string, {
        metadata: { Name: connectionName as string },
      });
      const authenticatedUser = paragon.getUser();
      return authenticatedUser.authenticated ? authenticatedUser : undefined;
    },
    {
      enabled: enabled && !!projectId && !!sdkToken && !isError && !!connectionName,
      // The SDK holds the session; re-authenticating on every mount is what this replaces.
      staleTime: Infinity,
    },
  );

  useEffect(() => {
    const listener = () => {
      const authenticatedUser = paragon.getUser();
      queryClient.setQueriesData(
        PARAGON_USER_QUERY_KEY,
        authenticatedUser.authenticated ? authenticatedUser : undefined,
      );
    };
    paragon.subscribe(SDK_EVENT.ON_INTEGRATION_INSTALL, listener);
    paragon.subscribe(SDK_EVENT.ON_INTEGRATION_UNINSTALL, listener);
    // Field mappings are saved inside the portal, which fires neither install event. Without this
    // the cached user still reports the mapping as it was before the user edited it.
    paragon.subscribe(SDK_EVENT.ON_PORTAL_CLOSE, listener);
    return () => {
      paragon.unsubscribe(SDK_EVENT.ON_INTEGRATION_INSTALL, listener);
      paragon.unsubscribe(SDK_EVENT.ON_INTEGRATION_UNINSTALL, listener);
      paragon.unsubscribe(SDK_EVENT.ON_PORTAL_CLOSE, listener);
    };
  }, [queryClient]);

  return { user, error: error instanceof Error ? error : undefined };
}
